import { useState, useEffect } from 'react';
import { differenceInSeconds } from 'date-fns';

interface CountdownProps {
  expirationTime?: string;
  invoiceExpirationTime?: string;
  onGenerateNewSession?: () => void;
  isGeneratingSession?: boolean;
}

const Countdown: React.FC<CountdownProps> = ({
  expirationTime,
  invoiceExpirationTime,
  onGenerateNewSession,
  isGeneratingSession,
}) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expirationTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const expiration = new Date(expirationTime);
      const totalSeconds = differenceInSeconds(expiration, now);

      if (totalSeconds <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        return;
      }

      setIsExpired(false);
      const days = Math.floor(totalSeconds / (24 * 60 * 60));
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [expirationTime]);

  if (isExpired) {
    const isInvoiceStillValid = invoiceExpirationTime
      ? differenceInSeconds(new Date(invoiceExpirationTime), new Date()) > 0
      : false;

    if (isInvoiceStillValid && onGenerateNewSession) {
      return (
        <div className="flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
          <span className="font-semibold text-red-600">
            This payment session expired. Please generate a new one to continue
            paying with this invoice.
          </span>
          <button
            type="button"
            onClick={onGenerateNewSession}
            disabled={isGeneratingSession}
            className="w-full flex-shrink-0 rounded-lg bg-error-main px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 md:w-max"
          >
            {isGeneratingSession ? 'Generating...' : 'Generate New Session'}
          </button>
        </div>
      );
    }

    return <span className="font-semibold text-red-600">Payment Expired</span>;
  }

  if (!timeLeft) {
    return <span>-</span>;
  }

  return (
    <div className="flex w-full flex-row items-center justify-between gap-1 md:justify-start">
      <div className="flex flex-row items-center gap-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-clock"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
          <path d="M12 7v5l3 3" />
        </svg>
        <span>Pay before:</span>
      </div>
      <span className="text-2xl font-semibold text-error-main">
        {timeLeft.days > 0 && `${timeLeft.days}d `}
        {String(timeLeft.hours).padStart(2, '0')}:
        {String(timeLeft.minutes).padStart(2, '0')}:
        {String(timeLeft.seconds).padStart(2, '0')}
      </span>
    </div>
  );
};

export default Countdown;
